import { put, delay, takeEvery, all, call, select } from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import { setPageTopLoader, setRealTimeMapData } from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import seasonConfig from '../../utils/pictree/SeasonDataConfig';

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTree;

const seasons = [
  {
      "seasonId": "cm64mqm930009r3n01z5xbr5d",
      "seasonName": "沼津シーズン5",
      "seasonStart": "2025-04-05",
      "seasonEnd": "2025-04-27"
  },
  {
      "seasonId": "cm64mqm920007r3n062h28osr",
      "seasonName": "沼津シーズン4",
      "seasonStart": "2025-03-22",
      "seasonEnd": "2025-04-04"
  },
  {
      "seasonId": "cm64mqm920005r3n03fh92tre",
      "seasonName": "沼津シーズン3",
      "seasonStart": "2025-03-08",
      "seasonEnd": "2025-03-21"
  },
  {
      "seasonId": "cm64mqm920003r3n0h0inau4q",
      "seasonName": "沼津シーズン2",
      "seasonStart": "2025-02-22",
      "seasonEnd": "2025-03-07"
  },
  {
      "seasonId": "cm64mqm910001r3n0do47dv2j",
      "seasonName": "沼津シーズン1",
      "seasonStart": "2025-02-01",
      "seasonEnd": "2025-02-21"
  }
];

function findSeasonIdsForRange(rangeStr, seasons) {
  const [from, to] = rangeStr.split("~").map(s => s.trim());
  // 選択レンジの JST 0:00〜23:59:59
  const startJst = moment.tz(from, "YYYY-MM-DD", "Asia/Tokyo").startOf("day");
  const endJst   = moment.tz(to,   "YYYY-MM-DD", "Asia/Tokyo").endOf("day");

  const matched = seasons.filter((s) => {
    // シーズン文字列も同様に JST にパース
    const sStartJst = moment.tz(s.seasonStart, "YYYY-MM-DD", "Asia/Tokyo")
                          .startOf("day");
    const sEndJst   = moment.tz(s.seasonEnd,   "YYYY-MM-DD", "Asia/Tokyo")
                          .endOf("day");

    // 「シーズンの開始 ≤ レンジ開始 AND シーズンの終了 ≥ レンジ終了」
    return sStartJst.isSameOrBefore(startJst) &&
           sEndJst  .isSameOrAfter( endJst);
  });

  return matched.map((s) => s.seasonId);
}

const findSeason = (targetDate) => {
  // Convert the targetDate to a moment object for comparison
  const target = moment(targetDate, "YYYY-MM-DD");

  for (let config of seasonConfig.seasonConfig) {
    const startDate = moment(config.dateRange[0], "YYYY-MM-DD");
    const endDate = moment(config.dateRange[1], "YYYY-MM-DD");

    // Check if the target date falls within the date range
    if (target.isBetween(startDate, endDate, null, '[]')) {
      return typeof config.season !== "number" ? config.season : `Season${config.season}`;
    }
  }
  return null; // If no matching season is found
};

const findGameSpaceId = (seasonName) => {
  const config = seasonConfig.gameSpaceConfig.find(item => item.name === seasonName);
  return config ? config.game_space_id : null;
};

export function* getPictreeRealTimeMapData(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const seasonName = findSeason(rangeTarget[1]);
    const property = action.payload.property;
    let propertyQury = property === "ntt" ? `AND property IN ('ntt', 'nttme')` : ``;
    if (property === "tepco" && action.payload.type === "denchu") {
      propertyQury = `AND property = 'tepco'`
    } else if (property === "nttft") {
      propertyQury = `AND property = 'nttft'`
    }

    const tableName    = (() => {
      switch (action.payload.type) {
        case "kankiko":        return "DenchuPhotoShootKankikoEvtProd";
        case "groundEquipiment": return "DenchuPhotoShootGeEvtProd";
        case "checkin":        return "DenchuCheckInEvtProd";
        default:               return "DenchuPhotoShootDenchuEvtProd";
      }
    })();

    let timeCondition;
    if (isNaN(action.payload.range)) {
      const seasonIds = findSeasonIdsForRange(action.payload.range, seasons);

      // 手動モード: 'YYYY-MM-DD HH:mm ~ YYYY-MM-DD HH:mm'
      const [from, to] = action.payload.range.split("~");
      const startTime = moment(from, "YYYY-MM-DD HH:mm").valueOf();
      const endTime   = moment(to,   "YYYY-MM-DD HH:mm").valueOf();
      timeCondition   = `logDate BETWEEN ${startTime} AND ${endTime}
      AND (gameSpaceId = '${seasonIds[0]}' OR gameSpaceId IS NULL)`;
    } else {
      // 自動モード: 今から N 分遡る
      timeCondition = `logDate >= (UNIX_TIMESTAMP(NOW() - INTERVAL ${action.payload.range} MINUTE) * 1000)`;
    }

    const whereClause = `WHERE ${timeCondition} ${propertyQury}`;

    const sqlCountMapData = {
        query: `
          SELECT COUNT(*)
          FROM ${tableName}
          ${whereClause};`,
        database: "KpiDashboard",
        type: "list",
      };

    const [dataPhotoCount] = yield all([
      call(getDataFromRDS, sqlCountMapData, ENDPOINT.GET_DATA_USERS, jwtToken),
    ]);
    const countPhoto = JSON.parse(dataPhotoCount.body);

    let recordArray = [], eachData = {};
    const LIMIT = 3000;
    let offset = 0;

    while (offset < countPhoto[0][0]["longValue"]) {
      const sqlQueryChunk = {
        query: `
          SELECT *
            FROM ${tableName}
          ${whereClause}
          ORDER BY logDate DESC
          LIMIT ${LIMIT} OFFSET ${offset};
        `,
        database: "KpiDashboard",
        type: "list",
      };
      const [dataChunk] = yield all([
        call(getDataFromRDS, sqlQueryChunk, ENDPOINT.GET_DATA_USERS, jwtToken),
      ]);
      const rows = JSON.parse(dataChunk.body);

      rows.forEach(cols => {
        recordArray.push({
          user:      cols[2].stringValue,
          asset_id:  cols[4].stringValue,
          latitude:  parseFloat(cols[5].stringValue),
          longitude: parseFloat(cols[6].stringValue),
          parts:     cols[7].stringValue,
          picCount:  cols[8].longValue,
          dateTime:  moment(cols[14].longValue)
                       .tz("Asia/Tokyo")
                       .format("YYYY/MM/DD hh:mm A"),
        });
      });

      offset += LIMIT;
    }

    // get untaken records
    let unTakenRecordArray = [], each = {};

    yield put(setRealTimeMapData([recordArray, unTakenRecordArray]));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export default function* watchPageSettings() {
  yield takeEvery(PICTREE.GET_REALTIME_MAP_DATA,getPictreeRealTimeMapData);
}
